'use client';

import type { AnomalyGraphData } from '@repo/api-gw-sdk';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Dot,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { dayjs } from '@/utils/dayjs';

import { AnomalyChartTooltip } from './anomalyChartTooltip';

interface AnomalyChartProps {
  graphData: AnomalyGraphData;
}

export const AnomalyChart = ({ graphData }: AnomalyChartProps) => {
  const dataPoints = graphData.dataPoints ?? [];
  if (dataPoints.length === 0) return null;

  const chartData = dataPoints.map((dp) => ({
    timestamp: dayjs.utc(dp.timestamp).format('MMM D'),
    actual: dp.actualValue,
    forecast: dp.forecastValue,
    upperBound: dp.upperBound,
    lowerBound: dp.lowerBound,
    isAnomaly: dp.isAnomaly,
    snapshotId: dp.snapshotId,
    // For the stacked area band (lower base + delta = shaded region)
    boundDelta: dp.upperBound - dp.lowerBound,
  }));

  return (
    <ResponsiveContainer width='100%' height={150}>
      <ComposedChart
        data={chartData}
        margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
      >
        <CartesianGrid stroke='var(--mui-palette-divider)' />
        <XAxis
          dataKey='timestamp'
          tickLine={false}
          axisLine={false}
          fontSize={10}
          tickMargin={6}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          fontSize={10}
          tickCount={4}
          domain={['auto', 'auto']}
        />
        <Tooltip
          cursor={{ fill: 'transparent' }}
          content={(props) => <AnomalyChartTooltip payload={props.payload} />}
        />

        {/* Shaded band between lower and upper bounds */}
        <Area
          type='monotone'
          dataKey='lowerBound'
          stackId='band'
          stroke='none'
          fill='transparent'
          isAnimationActive={false}
        />
        <Area
          type='monotone'
          dataKey='boundDelta'
          stackId='band'
          stroke='none'
          fill='var(--mui-palette-action-hover)'
          fillOpacity={0.4}
          isAnimationActive={false}
        />

        {/* Threshold lines (drawn after areas so they sit on top of the band) */}
        <Line
          type='monotone'
          dataKey='lowerBound'
          stroke='var(--mui-palette-divider)'
          strokeWidth={1.25}
          strokeDasharray='4 3'
          dot={false}
          isAnimationActive={false}
        />
        <Line
          type='monotone'
          dataKey='upperBound'
          stroke='var(--mui-palette-divider)'
          strokeWidth={1.25}
          strokeDasharray='4 3'
          dot={false}
          isAnimationActive={false}
        />

        {/* Forecast line */}
        <Line
          type='monotone'
          dataKey='forecast'
          stroke='var(--mui-palette-text-disabled)'
          strokeWidth={1.9}
          strokeDasharray='5 5'
          dot={false}
          isAnimationActive={false}
        />

        {/* Actual data line */}
        <Line
          type='monotone'
          dataKey='actual'
          stroke='var(--mui-palette-warning-main)'
          strokeWidth={2}
          dot={(dotProps) => {
            const { cx, cy, payload } = dotProps;
            if (payload?.isAnomaly) {
              return (
                <Dot
                  key={`anomaly-${cx}-${cy}`}
                  cx={cx}
                  cy={cy}
                  r={5}
                  fill='var(--mui-palette-error-main)'
                  stroke='var(--mui-palette-error-main)'
                />
              );
            }
            return <Dot key={`dot-${cx}-${cy}`} cx={cx} cy={cy} r={0} />;
          }}
          activeDot={(dotProps) => {
            const { cx, cy, payload } = dotProps;
            return (
              <Dot
                key={`active-${cx}-${cy}`}
                cx={cx}
                cy={cy}
                r={payload?.isAnomaly ? 7 : 4}
                fill={
                  payload?.isAnomaly
                    ? 'var(--mui-palette-error-main)'
                    : 'var(--mui-palette-warning-main)'
                }
                stroke={
                  payload?.isAnomaly
                    ? 'var(--mui-palette-error-main)'
                    : 'var(--mui-palette-warning-main)'
                }
              />
            );
          }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
